import { CardElement, IbanElement } from '@stripe/react-stripe-js'
import { ConfirmCardPaymentData, ConfirmSepaDebitPaymentData } from '@stripe/stripe-js'
import {
  DelayFn,
  ErrorHandler,
  FormErrors,
  FormFormikBag,
  FormSubmitHandler,
  PaymentResult,
  PaypalFinalizeHandler,
  RedirectHandler,
  StripeFinalizeHandler,
} from './types'
import { FormValues, StripeOptions } from 'donate/types'
import { JSONFetcher, dataLayer, postValues, report } from 'shared'
import { ReportableError } from '@betterplace/error-reporting'
import { useCallback } from 'react'
import { useStripeContext } from '../stripe_context'

const handleBackendErrors = (errors: FormErrors, formikBag: FormFormikBag) => {
  const errorMessages: FormErrors = Object.fromEntries(
    Object.entries(errors).map(([key, error]) => {
      const message =
        typeof error === 'string' ? i18n.t(['donate.errors', error]) : i18n.t('donate.errors.unknown_error')
      return [key, message]
    })
  )

  return void errorHandler(errorMessages, formikBag)
}

const submitHandler = async (values: FormValues, formikBag: FormFormikBag, stripeOptions?: StripeOptions) => {
  formikBag.setStatus(null)
  dataLayer.push({
    event: 'eeCheckout',
    ecommerce: { currencyCode: 'EUR', checkout: { actionField: { step: 2, option: values.payment_method } } },
  })
  const body = postValues(values)
  const response = await JSONFetcher.call<typeof body, PaymentResult>({
    url: values._submit_url,
    method: 'post',
    body,
  }).catch((e) => {
    // any server side error (500, 502, ...)
    // ignore 429 "too many requests"
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!/429/.test(e.message)) report(e)
    return { errors: { base: 'unknown_error' }, redirect_to: '' } as PaymentResult
  })

  const { paypal_token, stripe_payment_intent_client_secret, redirect_to, errors } = response

  if (errors) return handleBackendErrors(errors, formikBag)

  if (paypal_token) return paypalFinalize(formikBag, paypal_token, redirect_to)

  const catchError = (error: ReportableError) => {
    errorHandler({ payment_method: i18n.t('donate.errors.unknown_error') }, formikBag)
    report(error)
  }

  if (stripe_payment_intent_client_secret && values.payment_method === 'stripe_sepa_debit') {
    if (throttle()) return errorHandler({ stripe_token: i18n.t('donate.errors.throttled') }, formikBag)
    return await stripeSEPADebitFinalize(
      formikBag,
      values,
      stripe_payment_intent_client_secret,
      redirect_to,
      stripeOptions
    ).catch(catchError)
  }
  if (stripe_payment_intent_client_secret && values.payment_method === 'stripe_cc') {
    if (throttle()) return errorHandler({ stripe_token: i18n.t('donate.errors.throttled') }, formikBag)
    return await stripeCCFinalize(
      formikBag,
      values,
      stripe_payment_intent_client_secret,
      redirect_to,
      stripeOptions
    ).catch(catchError)
  }
  if (redirect_to) {
    return await redirect({ to: redirect_to, formikBag }).catch(catchError)
  }
}

const paypalFinalize: PaypalFinalizeHandler = (formikBag, paypalToken, redirectTo) => {
  void formikBag.setFieldValue('_paypal_token', paypalToken)
  void formikBag.setFieldValue('_redirect_to', redirectTo)
  // setting isSubmitting to false lets the PaypalButton know that it can enter the auth flow
  formikBag.setSubmitting(false)
}

let lastCallTime = 0

const throttle = () => {
  const currentCallTime = new Date().getTime()
  const calledRecently = currentCallTime - lastCallTime < 2
  lastCallTime = currentCallTime
  return calledRecently
}

const stripeCCFinalize: StripeFinalizeHandler = async (
  formikBag,
  values,
  paymentIntentClientSecret,
  redirectTo,
  stripeOptions
) => {
  const card = stripeOptions?.elements?.getElement(CardElement)

  if (!card) {
    // show generic error message if the card element doesn't exist
    errorHandler({ stripe_token: i18n.t('donate.errors.stripe_cc_unavailable') }, formikBag)
    return
  }

  const data: ConfirmCardPaymentData = {
    payment_method: {
      card,
      billing_details: {
        name: `${values.first_name} ${values.last_name}`,
      },
    },
  }

  const result = await stripeOptions?.stripe?.confirmCardPayment(paymentIntentClientSecret, data).catch((error) => {
    report(error)
  })
  const error = result?.error
  if (error && !/already succeeded/.test(error.message ?? '')) {
    const msg = i18n.t(`donate.errors.stripe_cc_${error.code ?? 'unknown_error'}`, { defaultValue: error.message })
    errorHandler({ stripe_token: msg }, formikBag)
  } else {
    await redirect({ to: redirectTo, formikBag })
  }
}

const stripeSEPADebitFinalize: StripeFinalizeHandler = async (
  formikBag,
  values,
  paymentIntentClientSecret,
  redirectTo,
  stripeOptions
) => {
  const iban = stripeOptions?.elements?.getElement(IbanElement)

  if (!iban) {
    // show generic error message if the iban element doesn't exist
    errorHandler({ stripe_token: i18n.t('donate.errors.stripe_sepa_debit_unavailable') }, formikBag)
    return
  }

  const data: ConfirmSepaDebitPaymentData = {
    payment_method: {
      sepa_debit: iban,
      billing_details: {
        name: `${values.first_name} ${values.last_name}`,
        email: values.email,
        // make sure that a given company_name appears in the sepa mandate that is generated by Stripe
        address:
          values._company_donation && values.company_name
            ? {
                city: values.city,
                line1: values.company_name,
                postal_code: values.zip,
              }
            : undefined,
      },
    },
  }

  const result = await stripeOptions?.stripe?.confirmSepaDebitPayment(paymentIntentClientSecret, data)
  const error = result?.error

  if (error && !/already succeeded/.test(error.message ?? '')) {
    const msg = i18n.t(`donate.errors.stripe_sepa_debit_${error.code ?? 'unknown_error'}`, {
      defaultValue: error.message,
    })
    errorHandler({ stripe_token: msg }, formikBag)
  } else {
    await redirect({ to: redirectTo, formikBag })
  }
}

const errorHandler: ErrorHandler = (errors, formikBag) => {
  void formikBag.setFieldValue('_step', 1, false)
  formikBag.setStatus({
    customErrors: errors,
  })
  formikBag.setSubmitting(false)
}

const redirect: RedirectHandler = async ({ to, formikBag }) => {
  // stay non-interactable while redirect is in progress
  // to prevent confusion and extraneous submits
  void formikBag.setFieldValue('_redirecting', true)
  return await delay(10, () => window.location.replace(to))
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const delay: DelayFn = (time, cb = () => {}) =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        cb()
        resolve(undefined)
      } catch (e) {
        reject(e)
      }
    }, time)
  })

export const useSubmitHandler = () => {
  const stripeOptions = useStripeContext()
  const handler: FormSubmitHandler = useCallback(
    (formikBag, values) => submitHandler(formikBag, values, stripeOptions),
    [stripeOptions]
  )
  return handler
}
